import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, History, Save, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatItDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const fieldLabel = (campo) => {
  const map = {
    ora_entrata: "Ora entrata",
    ora_uscita: "Ora uscita",
    data: "Data",
    turno_riferimento: "Turno",
    note: "Note",
    assente: "Assente",
    ritardo: "Ritardo",
    uscita_anticipata: "Uscita anticipata",
  };
  return map[campo] || campo;
};

const formatVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Sì" : "No";
  return String(v);
};

const EditPresenzaDialog = ({
  open,
  presenza,
  operatoreNome,
  data,
  getAuthHeaders,
  onClose,
  onSaved,
}) => {
  const [oraEntrata, setOraEntrata] = useState("");
  const [oraUscita, setOraUscita] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (presenza) {
      setOraEntrata(presenza.ora_entrata || "");
      setOraUscita(presenza.ora_uscita || "");
      setNote(presenza.note || "");
      setShowHistory(false);
    }
  }, [presenza]);

  if (!presenza) return null;

  const modifiche = presenza.modifiche || [];
  const hasHistory = modifiche.length > 0;

  const handleSave = async () => {
    if (!presenza.id) {
      toast.error("Nessuna presenza esistente — l'operatore deve prima timbrare");
      return;
    }
    setSaving(true);
    try {
      const body = {
        ora_entrata: oraEntrata || null,
        ora_uscita: oraUscita || null,
        note: note || null,
      };
      const res = await axios.put(`${API}/personale/presenze/${presenza.id}`, body, {
        headers: getAuthHeaders(),
      });
      if (res.data?.message === "Nessuna modifica") {
        toast.info("Nessuna modifica rilevata");
      } else {
        toast.success(`Salvato. ${res.data?.modifiche || 0} modifiche tracciate.`);
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-amber-500" />
            Modifica presenza
          </DialogTitle>
          <DialogDescription className="text-xs">
            <strong>{operatoreNome}</strong> · {data}
            <br />
            <span className="text-amber-600 font-medium">
              Ogni modifica viene tracciata con nome admin, data e ora.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="oraE" className="text-xs">
                Ora entrata
              </Label>
              <Input
                id="oraE"
                type="time"
                value={oraEntrata}
                onChange={(e) => setOraEntrata(e.target.value)}
                data-testid="edit-ora-entrata"
              />
            </div>
            <div>
              <Label htmlFor="oraU" className="text-xs">
                Ora uscita
              </Label>
              <Input
                id="oraU"
                type="time"
                value={oraUscita}
                onChange={(e) => setOraUscita(e.target.value)}
                data-testid="edit-ora-uscita"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="note" className="text-xs">
              Note (opzionale)
            </Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Es. dimenticata timbratura entrata, recuperata manualmente"
              data-testid="edit-note"
            />
          </div>

          {/* History */}
          {hasHistory && (
            <div className="border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => setShowHistory((s) => !s)}
                className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900"
                data-testid="toggle-history-btn"
              >
                <History className="h-3.5 w-3.5" />
                Storico modifiche ({modifiche.length})
                <span className="text-gray-400">{showHistory ? "▲" : "▼"}</span>
              </button>
              {showHistory && (
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto" data-testid="history-list">
                  {modifiche
                    .slice()
                    .reverse()
                    .map((m, idx) => (
                      <div
                        key={idx}
                        className="text-xs p-2 rounded-lg bg-amber-50 border border-amber-100"
                      >
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                          <span className="font-bold text-gray-900">
                            {m.modificato_da_nome || "—"}
                          </span>
                          <span className="text-[10px] text-gray-500">
                            {formatItDate(m.modificato_at)}
                          </span>
                        </div>
                        {(m.diff || []).map((d, i) => (
                          <div key={i} className="text-[11px] text-gray-700">
                            <span className="font-semibold">{fieldLabel(d.campo)}:</span>{" "}
                            <span className="text-red-600 line-through">{formatVal(d.prima)}</span>
                            {" → "}
                            <span className="text-emerald-600 font-semibold">
                              {formatVal(d.dopo)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Annulla
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-edit-btn">
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Salva modifica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditPresenzaDialog;
